/**
 * Derive avatar presentation (initials, display name, colour) for the
 * authenticated user shown in the sidebar account control.
 */

/** Minimal shape of the authenticated user needed to render an avatar. */
export interface AvatarUser {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

/** Rendered avatar fields for the account control. */
export interface AvatarDisplay {
  initials: string;
  name: string;
  color: string;
}

/**
 * Stable palette for user avatars. A user always maps to the same colour so the
 * badge is recognisable across sessions.
 */
const AVATAR_PALETTE = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EF4444",
  "#0EA5E9",
  "#EC4899",
  "#14B8A6",
];

/**
 * Split the local part of an email into name-like segments.
 *
 * @param email - Full email address (may be null/empty).
 * @returns Lower-case segments split on common separators, empties removed.
 */
function emailNameSegments(email?: string | null): string[] {
  const local = (email ?? "").split("@")[0] ?? "";
  return local.split(/[._+-]+/).filter(Boolean);
}

/**
 * Build up-to-two-letter initials for a user.
 *
 * Prefers first + last name; otherwise derives them from the email local part
 * (e.g. `percy.yeung@…` → `PY`). Returns an empty string when nothing usable is
 * available so callers can fall back to a default.
 *
 * @param user - Authenticated user, or null when unavailable.
 * @returns Upper-case initials (0–2 characters).
 */
export function deriveInitials(user: AvatarUser | null | undefined): string {
  const first = user?.firstName?.trim() ?? "";
  const last = user?.lastName?.trim() ?? "";
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  const segments = emailNameSegments(user?.email);
  if (segments.length >= 2) {
    return `${segments[0].charAt(0)}${segments[1].charAt(0)}`.toUpperCase();
  }
  if (segments.length === 1) {
    return segments[0].slice(0, 2).toUpperCase();
  }
  return "";
}

/**
 * Build a human-readable display name for a user.
 *
 * @param user - Authenticated user, or null when unavailable.
 * @returns Full name, else the email, else an empty string.
 */
export function deriveName(user: AvatarUser | null | undefined): string {
  const full = `${user?.firstName?.trim() ?? ""} ${user?.lastName?.trim() ?? ""}`.trim();
  return full || (user?.email?.trim() ?? "");
}

/**
 * Pick a deterministic avatar colour for a user from {@link AVATAR_PALETTE}.
 *
 * @param key - Stable identity key (email or name).
 * @returns A hex colour string from the palette.
 */
export function colorForKey(key: string): string {
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

/**
 * Compose the avatar display for an authenticated user, falling back to the
 * supplied defaults when no user (or no usable fields) is available.
 *
 * @param user - Authenticated user, or null when unavailable.
 * @param fallback - Default avatar to use when the user is missing fields.
 * @returns Initials, name, and colour ready for rendering.
 */
export function resolveAvatar(
  user: AvatarUser | null | undefined,
  fallback: AvatarDisplay,
): AvatarDisplay {
  const initials = deriveInitials(user);
  if (!initials) return fallback;
  const key = user?.email?.trim() || deriveName(user) || initials;
  return {
    initials,
    name: deriveName(user) || fallback.name,
    color: colorForKey(key),
  };
}
